// auth.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../environments/environment';
import { BehaviorSubject, Observable, Subject, throwError, timer } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { AppData } from './data-store';

@Injectable({
  providedIn: 'root'
})
export class OauthService {
  private _tokenObtained = new BehaviorSubject<boolean>(false);
  tokenObtained = this._tokenObtained.asObservable();
  private _onLogout = new BehaviorSubject<boolean>(false);
  onLogout = this._onLogout.asObservable();
  refreshTokenInterval: any = null;
  
  private _cancelPendingRequests = new Subject<void>();
  cancelPendingRequests = this._cancelPendingRequests.asObservable();
  constructor(private http: HttpClient) { }

  /**
   * the first method to invoke when try to login
   */
  redirectToKeycloak(idpHint?:string) {
    const { url, realm, clientId, context } = environment.keycloak;
    const redirectUriEncoded = encodeURIComponent(this.getRedirectUri());
    const authUrl = `${url}/${context}/realms/${realm}/protocol/openid-connect/auth` +
                    `?kc_idp_hint=${idpHint}&response_type=code&scope=openid&response_mode=query&client_id=${clientId}&redirect_uri=${redirectUriEncoded}`;
    window.location.href = authUrl;
  }

  /**
   * notifies App Component that Access token has been given
   * @param param 
   */
  onAccessTokenObtained(param: boolean) {
    this._tokenObtained.next(param);
  }

  refreshAccessToken(): Observable<any> {
    const { url, realm, clientId, context } = environment.keycloak;
    const body = new HttpParams()
      .set('grant_type', "refresh_token")
      .set('client_id', clientId)
      .set('refresh_token', localStorage.getItem('refresh_token'))
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    return this.http
    .post<any>(`${url}/${context}/realms/${realm}/protocol/openid-connect/token`, body.toString(), { headers })
    .pipe(
      map(response => response),
      // Fix #1: throwError rilancia correttamente come Observable di errore
      // invece di restituire l'oggetto errore come valore normale
      catchError(error => {
        if (error.status === 400) { }
        return throwError(() => error);
      })
    );
  }

  getAccessToken(code: string): Observable<AccessToken> {
    const { url, realm, clientId, context } = environment.keycloak;
    const tenant = AppData.getInstance().TNT_TENANT;
    localStorage.setItem("OAUTH_TENANT", tenant);
    const body = new HttpParams()
      .set('grant_type', "authorization_code")
      .set('code', code)
      .set('redirect_uri', this.getRedirectUri(tenant?.toLowerCase()))
      .set('client_id', clientId);
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    return this.http
      .post<any>(`${url}/${context}/realms/${realm}/protocol/openid-connect/token`, body.toString(), { headers })
      .pipe(
        map(response => response),
        tap(() => localStorage.setItem("OAUTH_TENANT", tenant)),
        catchError(error => {
          console.error('Error exchanging code for token:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Verifica la validità del token JWT in localStorage.
   *
   * Miglioramenti rispetto alla versione precedente:
   * - Null-check esplicito prima di qualsiasi operazione sulla stringa,
   *   evitando che `null.split(...)` lanci un'eccezione silenziosa
   * - Verifica strutturale del JWT (esattamente 3 parti)
   * - Controllo esplicito del campo `exp` prima di confrontarlo
   */
  hasValidToken(): boolean {
    const accessToken = localStorage.getItem("access_token");

    // Token assente o strutturalmente non valido: nessuna eccezione
    if (!accessToken || !accessToken.includes('.')) return false;

    try {
      const parts = accessToken.split('.');
      // Un JWT valido ha esattamente 3 parti: header.payload.signature
      if (parts.length !== 3) return false;

      const payloadAccess = JSON.parse(atob(parts[1]));

      // Controlla che il campo exp esista prima di confrontarlo
      if (!payloadAccess?.exp) return false;

      const currentTimestamp = Math.floor(Date.now() / 1000);
      return payloadAccess.exp > currentTimestamp;
    } catch {
      return false;
    }
  }

  /**
   * @POST
   * invalidates Keykloack Session
   * @returns 
   */
  async invalidateTokenSession() {
    const { url, realm, clientId, context } = environment.keycloak;
    const id_token = localStorage.getItem("id_token");
    const redirectUriEncoded = encodeURIComponent(url);
    let _url = `${url}/${context}/realms/${realm}/protocol/openid-connect/logout?client_id=${clientId}&post_logout_redirect_uri=${redirectUriEncoded}&id_token_hint=${id_token}`;
    this.clearOAuthData();
    window.location.href = _url;
  }

  /**
   * clears client session 
   */
  clearOAuthData() {
    localStorage.clear();
    sessionStorage.clear();
    this._onLogout.next(true);
  }

  public onCancelPendingRequests() {
    this._cancelPendingRequests.next();
  }

  private getRedirectUri(ten?) {
    let tenant = ten ? ten : AppData.getInstance().TNT_TENANT;
    if(!!tenant) {
      return window.location.protocol + '//' + window.location.host + "/" + tenant +'/teleforumweb';
    } else {
      return window.location.protocol + '//' + window.location.host;
    }
  }

  public isTokenIdValid(): boolean {
    const tokenId = localStorage.getItem("id_token");
    if (!tokenId || tokenId === undefined || tokenId === "") return false;
    try {
      const payload = tokenId.split('.')[1];
      const decodedPayload = JSON.parse(this.base64UrlDecode(payload));
      const exp = decodedPayload.exp;
      if (!exp) return false;
      const now = Math.floor(Date.now() / 1000);
      return exp > now;
    } catch (err) {
      console.error('Errore durante il decoding manuale del token', err);
      return false;
    }
  }

  private base64UrlDecode(str: string): string {
    const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
    return atob(padded);
  }

}

export interface AccessToken {
  access_token: string;
  expires_in: number;
  refresh_expires_in: number;
  refresh_token: string;
  scope: string;
  token_type: string;
  session_state: string;
  id_token: string;
}